import { useAuth, type TaxRegime } from "../../store/auth";

/**
 * Resolves the tenant's tax regime + UI-ready labels. Components that show
 * tax-related copy ("GST 12%", "VAT Reg. No.", "GSTR-3B Return") call this
 * once instead of branching on the regime string ad-hoc.
 *
 *   const { regime, taxLabel, taxIdLabel, isGst, isVat } = useTaxRegime();
 *
 * Defaults to `"Gst"` if a stale auth payload (pre-Phase 4) is rehydrated
 * from localStorage without `taxRegime`.
 */
export function useTaxRegime(): {
  regime: TaxRegime;
  /** "GST" / "VAT" / "Tax" — column headers, banners. */
  taxLabel: string;
  /** "GSTIN" / "VAT Reg. No." / "Tax ID" — customer/vendor form labels. */
  taxIdLabel: string;
  /** Convenience guards for show/hide blocks. */
  isGst: boolean;
  isVat: boolean;
  isNone: boolean;
} {
  const regime: TaxRegime = useAuth((s) => s.user?.taxRegime ?? "Gst");

  switch (regime) {
    case "Vat":
      return {
        regime,
        taxLabel: "VAT",
        taxIdLabel: "VAT Reg. No.",
        isGst: false,
        isVat: true,
        isNone: false,
      };
    case "None":
      return {
        regime,
        taxLabel: "Tax",
        taxIdLabel: "Tax ID",
        isGst: false,
        isVat: false,
        isNone: true,
      };
    case "Gst":
    default:
      return {
        regime: "Gst",
        taxLabel: "GST",
        taxIdLabel: "GSTIN",
        isGst: true,
        isVat: false,
        isNone: false,
      };
  }
}
